import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import {
  fetchPhnSubscriptionPlan,
  fetchPhnSubscriptionPlans,
  createPhnSubscriptionPlan,
  updatePhnSubscriptionPlan
} from "../../actions/callrecorder/phnSubscriptionPlansActions";

export interface PhnSubscriptionPlanInterface {
  url: string;

  id: number,

  name: string,
  incoming_sms_limit: number,
  active: boolean,

  created_at: string,
  updated_at: string
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  newParams: PhnSubscriptionPlanInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null
};

const adaptor = createEntityAdapter<PhnSubscriptionPlanInterface>();

const slice = createSlice({
  name: 'phnSubscriptionPlans',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchPhnSubscriptionPlans.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchPhnSubscriptionPlans.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.phnSubscriptionPlans;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchPhnSubscriptionPlans.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchPhnSubscriptionPlan.fulfilled, (state, action) => {
        const plan = Object.values(action.payload.phn_subscription_plan)[0];
        state.entities[plan.id] = plan;
      })

      // #create
      .addCase(createPhnSubscriptionPlan.fulfilled, (state, action) => {
        const plan = Object.values(action.payload.phn_subscription_plan)[0];
        state.entities[plan.id] = plan;
      })
      .addCase(createPhnSubscriptionPlan.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updatePhnSubscriptionPlan.fulfilled, (state, action) => {
        const plan = Object.values(action.payload.phn_subscription_plan)[0];
        state.entities[plan.id] = plan;
      })
      .addCase(updatePhnSubscriptionPlan.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setEditError,
  setNewParams,
  setNewError
} = slice.actions;

export const {
  selectById: selectPhnSubscriptionPlanById,
  selectIds: selectPhnSubscriptionPlanIds,
  selectEntities: selectPhnSubscriptionPlanEntities,
}  = adaptor.getSelectors<RootState>(state => state.callrecorder.phnSubscriptionPlans);
